import { EmptyStateTable } from '@components'

import { Table } from '@shinetools/lumen-react'
import type { TableSortDirection } from '@shinetools/lumen-react'
import { ReactElement, ReactNode, useCallback, useState } from 'react'

import { LedgerEntryColumn } from '@features/balanceLedger/enums/ledgerEntryColumn'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LEDGER_ENTRIES_TABLE_MIN_WIDTH } from '../../../../constants/ledgerEntriesColumnWidths'
import { SuppliersBalanceEmptyState } from '../../../../elements/SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import type { ItemsPerPage } from '../../../../elements/TableFooter'
import { TableFooter } from '../../../../elements/TableFooter'
import { TableHeader } from '../../../../elements/TableHeader'
import { useLedgerEntriesTableColumns } from '../../../../hooks/useLedgerEntriesTableColumns'
import type { LedgerEntriesSortState } from '../../../../types/LedgerEntriesSortState'
import type { LedgerEntry } from '../../../../types/LedgerEntry'
import { LedgerEntriesToolbar } from '../LedgerEntriesToolbar'
import { LedgerEntryRow } from './elements/LedgerEntryRow'
import { OpeningBalanceGroupHeader } from './elements/OpeningBalanceGroupHeader'
import { PeriodTotalsFoot } from './elements/PeriodTotalsFoot'
import * as Styled from './styles'

const isLedgerEntryColumn = (columnId: string): columnId is LedgerEntryColumn =>
  (Object.values(LedgerEntryColumn) as string[]).includes(columnId)

interface LedgerEntriesPagination {
  currentPage: number
  itemsPerPage: ItemsPerPage
  totalPageItems: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: ItemsPerPage) => void
}

interface LedgerEntriesTotals {
  closingBalance: number
  periodEndDate?: string
  periodMovement: number
}

interface LedgerEntriesTableProps {
  data: LedgerEntry[]
  openingBalance?: number
  pagination: LedgerEntriesPagination
  hasActiveSearch: boolean
  isLoading?: boolean
  ledgerSearchQuery?: string
  onSearchChange: (ledgerSearchQuery?: string) => void
  onSort: (columnId: LedgerEntryColumn, isDesc: boolean) => void
  queryKey: string
  sortState?: LedgerEntriesSortState
  totals?: LedgerEntriesTotals
  emptyRowInfo?: ReactNode
}

export const LedgerEntriesTable = ({
  data,
  openingBalance,
  pagination,
  hasActiveSearch,
  isLoading,
  ledgerSearchQuery,
  onSearchChange,
  onSort,
  queryKey,
  sortState,
  totals,
  emptyRowInfo,
}: LedgerEntriesTableProps): ReactElement => {
  const { currentPage, itemsPerPage, totalPageItems, onPageChange, onItemsPerPageChange } = pagination
  const { organization } = useUserOrganization()
  const columns = useLedgerEntriesTableColumns(organization?.baseCurrencyId)
  // Several ledger entries can belong to the same transaction, so the open transaction alone can't tell which row was clicked.
  const [clickedEntryId, setClickedEntryId] = useState<string | undefined>(undefined)

  const handleSort = useCallback(
    (columnId: string) => {
      if (!isLedgerEntryColumn(columnId)) {
        return
      }
      const isCurrentColumn = sortState?.columnId === columnId
      onSort(columnId, isCurrentColumn ? !sortState.isDesc : true)
    },
    [onSort, sortState],
  )

  const getSortDirection = useCallback(
    (columnId: LedgerEntryColumn): TableSortDirection => {
      if (sortState?.columnId !== columnId) {
        return false
      }
      return sortState.isDesc ? 'descending' : 'ascending'
    },
    [sortState],
  )

  // A fiscal-year-start statement lists the carried-in (AN) rows as ordinary entries and an opening balance of 0.
  // Any other period sends the opening amount alone, which a search hides because it is not an entry.
  const hasOpeningBalanceRow = !hasActiveSearch && Boolean(openingBalance)
  const hasData = data.length > 0 || hasOpeningBalanceRow
  // The totals describe the period, not the matching rows, so a search with no match keeps the table and its totals.
  const showEmptyStateTable = !hasData && !hasActiveSearch && !isLoading
  const emptyState = emptyRowInfo ?? <SuppliersBalanceEmptyState />

  const renderRow = (entry: LedgerEntry): ReactElement => (
    <LedgerEntryRow
      key={entry.id}
      clickedEntryId={clickedEntryId}
      columns={columns}
      entry={entry}
      onEntryClick={setClickedEntryId}
    />
  )

  const renderTableBody = (): ReactElement => (
    <Table.Body>
      {hasOpeningBalanceRow && <OpeningBalanceGroupHeader columns={columns} totalBalance={openingBalance ?? 0} />}
      {data.map(renderRow)}
      {!hasData && (
        <Table.Row>
          <Table.Cell colSpan={columns.length}>{emptyState}</Table.Cell>
        </Table.Row>
      )}
    </Table.Body>
  )

  const tableHeader = <TableHeader columns={columns} getSortDirection={getSortDirection} onSort={handleSort} />
  const scrollResetKey = [
    queryKey,
    currentPage,
    itemsPerPage,
    sortState?.columnId ?? '',
    sortState?.isDesc ?? '',
    ledgerSearchQuery ?? '',
  ].join('-')

  return (
    <Styled.LedgerEntriesTableWrapper>
      <Styled.LedgerEntriesToolbarSlot>
        <LedgerEntriesToolbar ledgerSearchQuery={ledgerSearchQuery} onSearchChange={onSearchChange} />
      </Styled.LedgerEntriesToolbarSlot>
      <Styled.LedgerEntriesTableScrollArea>
        {showEmptyStateTable ? (
          <EmptyStateTable header={tableHeader} minWidth={LEDGER_ENTRIES_TABLE_MIN_WIDTH}>
            {emptyState}
          </EmptyStateTable>
        ) : (
          <Table compact>
            <Table.Content
              maxHeight="none"
              scrollable
              scrollResetKey={scrollResetKey}
              style={{ minWidth: LEDGER_ENTRIES_TABLE_MIN_WIDTH }}
            >
              {tableHeader}
              {isLoading ? <Table.Skeleton columns={columns.length} /> : renderTableBody()}
              {totals && !isLoading && (
                <PeriodTotalsFoot
                  closingBalance={totals.closingBalance}
                  columns={columns}
                  periodEndDate={totals.periodEndDate}
                  periodMovement={totals.periodMovement}
                />
              )}
            </Table.Content>
            {hasData && (
              <TableFooter
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={onItemsPerPageChange}
                onPageChange={onPageChange}
                totalPageItems={totalPageItems}
              />
            )}
          </Table>
        )}
      </Styled.LedgerEntriesTableScrollArea>
    </Styled.LedgerEntriesTableWrapper>
  )
}
